import { useState } from "react"
import { krdetailConstants } from '../../constants/krdetails.constants';

// Child MIT, or an MIT-sourced KR — mirrors krgraph.component.js isMITKR checks.
export const isMitKr = (
	metric: Partial<Metric>,
	metricObj?: Partial<{ source?: number | string }>
) => {
	const mirrorSource = metric?.mirrorSource as unknown as number;
	const objSource = metricObj?.source as unknown as number;
	const metricSource = metric?.source as unknown as number;

	return (
		!!metric?.child_mit ||
		!!metric?.mirrorChildMIT || // a mirror follows the KR it mirrors (mirrorSource covers its MIT source types)
		objSource == krdetailConstants.SOURCE_MIT_PERSON ||
		objSource == krdetailConstants.SOURCE_MIT_DATA_STREAMS ||
		objSource == krdetailConstants.SOURCE_DISTINCT_KR ||
		mirrorSource == krdetailConstants.SOURCE_MIT_PERSON ||
		mirrorSource == krdetailConstants.SOURCE_MIT_DATA_STREAMS ||
		mirrorSource == krdetailConstants.SOURCE_DISTINCT_KR ||
		metricSource == krdetailConstants.SOURCE_MIT_PERSON ||
		metricSource == krdetailConstants.SOURCE_MIT_DATA_STREAMS ||
		metricSource == krdetailConstants.SOURCE_DISTINCT_KR
	);
};

const enabledForKrType = (metric: Partial<Metric>) => {
	const mirrorSource = metric?.mirrorSource as unknown as number;
	const source = mirrorSource || metric?.source as unknown as number;
	const progressType = metric?.progress_type as unknown as number;

	// all kr valid progress types are supported
	const isSupportedKr = (
		progressType == krdetailConstants.PROGRESS_TYPE_TOTAL ||
		progressType == krdetailConstants.PROGRESS_TYPE_LAST_UPDATE ||
		progressType == krdetailConstants.PROGRESS_TYPE_AVERAGE
	);

	// UNSUPPORTED KR TYPES
	const isUnsupportedKr = (
		source == krdetailConstants.SOURCE_SURVEY || // survey krs are deprecated and not supported
		source == krdetailConstants.SOURCE_OTHER_GOALS // roll-up objectives are not supported
	)

	return isSupportedKr && !isUnsupportedKr;
}

/** MIT enhanced-chart behavior ships with new RAG; non-MIT KRs are unaffected. */
export const isMitEnhancedChartEnabled = (
	metric: Partial<Metric>,
	metricObj?: Partial<{ source?: number | string }>
) => !isMitKr(metric, metricObj) || !!window.wbConfig?.isNewRagEnabled;

/** Popup rows lack child_mit; derive it as Goal::getAllMetricsByGoal does (parentMId set, no parent). */
export const withChildMit = (metrics: any) => ({
	...metrics,
	child_mit: metrics.child_mit || (!!metrics.parentMId && !metrics.parent),
});

/** Same decision as useEnhancedKrChartToggle, without the hook, for callers that only need to branch. */
export const resolveShowEnhancedKrChart = (
	metric: Partial<Metric>,
	metricObj?: Partial<{ source?: number | string }>
) => {
	const persistedValue = window.sessionStorage.getItem('wobo-enhanced-kr-chart-enable-key');
	const show = persistedValue !== undefined && persistedValue !== null
		? persistedValue === 'true'
		: enabledForKrType(metric);
	return !!window.wbConfig?.isEnhancedKrChartEnabled && show && isMitEnhancedChartEnabled(metric, metricObj);
};

export const useEnhancedKrChartToggle = (
	metric: Partial<Metric>,
	metricObj?: Partial<{ source?: number | string }>
) => {
	const storageKey = 'wobo-enhanced-kr-chart-enable-key'
	const persistedValue = window.sessionStorage.getItem(storageKey);
	const isPersisted = persistedValue !== undefined && persistedValue !== null;
	const showEnhancedByDefaultForKr = enabledForKrType(metric);
	const [refreshKey, setRefreshKey] = useState(Date.now());
	const { isEnhancedKrChartEnabled, isNewRagEnabled } = window.wbConfig;
	const showEnhancedKrChart = isPersisted ? persistedValue === 'true' : showEnhancedByDefaultForKr;
	const mitEnhancedChartEnabled = isMitEnhancedChartEnabled(metric, metricObj);

	const setShowEnhancedKrChart = (enable: boolean) => {
		window.sessionStorage.setItem(storageKey, String(!!enable))
		setRefreshKey(Date.now())
	}
	
	window.__DEBUG_KrChart = {
		getStatus: () => ({
			isEnhancedKrChartEnabled,
			isNewRagEnabled,
			mitEnhancedChartEnabled,
			storageKey,
			isPersisted,
			persistedValue,
			showEnhancedByDefaultForKr,
			refreshKey,
			showEnhancedKrChart:
				isEnhancedKrChartEnabled && showEnhancedKrChart && mitEnhancedChartEnabled,
			metric
		}),
		toggleEnhancedKrChart: (enable) => {
			const enabled = enable === undefined ? !showEnhancedKrChart : !!enable;
			setShowEnhancedKrChart(enabled)
		},
		clearEnhancedKrChartToggle: () => {
			window.sessionStorage.removeItem(storageKey)
			setRefreshKey(Date.now())
		}
	}
	
	return {
		showEnhancedKrChart:
			isEnhancedKrChartEnabled && showEnhancedKrChart && mitEnhancedChartEnabled,
		shouldRefreshLegacyChart: !!refreshKey,
		onRefreshLegacyChart: () => setRefreshKey(null),
		setShowEnhancedKrChart
	}
}

type Metric = {
	id: string;
	name: string;
	achieveTarget: number;
	target: string;//## - ##
	targetDisplay: boolean;
	type: string;//enum
	progress: string;//#.#
	progress_type: string;//enum
	progressColor: string;
	status: string;//enum
	weightage: string;
	weightage_display: string;
	owner: string;//#
	real_owner: string;//#
	cascade_goal_id: unknown;
	goal_id: string;
	parent_goal_id: unknown;
	parent: unknown;
	ownerName: string;
	realOwnerName: string;
	source: string;//enum
	exclude: boolean;
	description: string;
	showAttachment: boolean;
	showComment: boolean;
	child_mit: boolean;
	parentMId: string;
	interval: string;//enum
	created_by: string;//#
	ext_app_id: unknown;
	mirrorId: string;
	mirrorSource: string;//enum
	reacted_emoji: unknown[];
	pingOwnerid: string;
	pingOwnerName: string;
	mitchildOnly: boolean;
	committed_target: string;//##
	mirrorChildMIT: boolean;
	krTags: unknown[];
	start_date: string;
	end_date: string;
	is_stretch_enabled: boolean;
	stretch_target: number;
	isQuarter: boolean;
	quarter_name: string;
	last_update: string;//####
	_current_last_update: string;//####
	_cache_last_update: unknown;
	offset_update: unknown;
	metric: string; //enum
	result_direction: string;
	initial_data: string;//#
	has_initial_data: boolean;
	has_stretch: boolean;
	stretchTargetTextDisplay: number;
	upper_limit: string;
	lower_limit: string;
	counting: string;//enum
	weighted_progress: string;
	metricStartDate: string;
	metricEndDate: string;
	hideRAGGraph: boolean;
	ragStartDate: string;
	ragEndDate: string;
	progress_color_raw: string;//#
	totalTargetTextDisplay: number;
	ragThreshold: string[];
	metricDaysLeft: number;
	daysOverPer: number;
	isQuarterStarted: boolean;
	hasWeightage: boolean;
	krWeightageEnabled: boolean;
	displayDateText: string;
	canEdit: boolean;
	canUpdateConfidence: boolean;
	canUpdate: boolean;
	sendPing: boolean;
	resultDet: boolean;
	krDependentCount: number;
	krAlignmentCount: number;
	krRelationship: number;
	canFetchDataFromExtApp: boolean;
	meetingAgenda: number;
	oneOnOneAgenda: number;
	hasStrategyEnabled: boolean;
}